import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { trackingHealth } from "../../../../helpers/api/tracking-health";
import { ShowToast } from "../../../../components/toast";
import type {
  SilenceCause,
  ConditionReasonCategory,
  ConditionScopeType,
  DiagnoseRunOutcome,
  FixStepAction,
  HistoryLog,
  HotelListFilters,
  IncidentStatus,
  OverrideState,
  TrackingAccessMode,
  TrackingLockKind,
  TrackingSettingsPayload,
  UpdateTrackingSettingsBody,
  WorklistFilters,
} from "../types";

/** Query keys, namespaced `["th", …]` (spec §8). */
export const thKeys = {
  all: ["th"] as const,
  summary: ["th", "summary"] as const,
  fleetMetrics: ["th", "fleet-metrics"] as const,
  worklist: (f: WorklistFilters) => ["th", "worklist", f] as const,
  hotels: (f: HotelListFilters) => ["th", "hotels", f] as const,
  hotel: (customerId: string) => ["th", "hotel", customerId] as const,
  ruleEvaluations: (customerId: string, rule?: string) =>
    ["th", "hotel", customerId, "rule-evaluations", rule ?? ""] as const,
  lockPreview: (customerId: string, lock: TrackingLockKind) =>
    ["th", "hotel", customerId, "lock-preview", lock] as const,
  incident: (incidentId: string) => ["th", "incident", incidentId] as const,
  run: (runId: string) => ["th", "run", runId] as const,
  engines: ["th", "engines"] as const,
  queues: ["th", "queues"] as const,
  /** The "Still onboarding" section's hidden rows — a durable, fleet-wide
   *  list since 2026-08-17 (see `hidden-rows.ts`, which owns the hook). */
  onboardingHidden: ["th", "onboarding-hidden"] as const,
  /** The fleet-wide settings row (backend ADR 0039) — one object, no params. */
  settings: ["th", "settings"] as const,
};

const STALE_MS = 60_000;

// ─── Reads ───────────────────────────────────────────────────────────────────

export function useFleetSummary() {
  return useQuery({
    queryKey: thKeys.summary,
    queryFn: trackingHealth.summary,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
  });
}

/** The census metric columns — a facts-store read the fleet table joins to
 *  the hotels read by customerId. Failure degrades the columns, never the
 *  table. */
export function useFleetMetrics() {
  return useQuery({
    queryKey: thKeys.fleetMetrics,
    queryFn: trackingHealth.fleetMetrics,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useWorklist(filters: WorklistFilters, enabled = true) {
  return useQuery({
    queryKey: thKeys.worklist(filters),
    queryFn: () => trackingHealth.worklist(filters),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useHotelList(filters: HotelListFilters, enabled = true) {
  return useQuery({
    queryKey: thKeys.hotels(filters),
    queryFn: () => trackingHealth.hotels(filters),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useHotelDetail(customerId: string) {
  return useQuery({
    queryKey: thKeys.hotel(customerId),
    queryFn: () => trackingHealth.hotelDetail(customerId),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    enabled: !!customerId,
  });
}

export function useRuleEvaluations(
  customerId: string,
  rule?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: thKeys.ruleEvaluations(customerId, rule),
    queryFn: () => trackingHealth.ruleEvaluations(customerId, { rule }),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    enabled: enabled && !!customerId,
  });
}

/** Fetched when the removal confirm opens — computed by the removal's own
 *  dry-run path so the confirm copy and the act cannot disagree. */
export function useLockRemovalPreview(
  customerId: string,
  lock: TrackingLockKind,
  enabled: boolean,
) {
  return useQuery({
    queryKey: thKeys.lockPreview(customerId, lock),
    queryFn: () => trackingHealth.lockRemovalPreview(customerId, lock),
    // Always current when the confirm opens — the consequence copy must
    // match the act.
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: enabled && !!customerId,
  });
}

export function useIncident(incidentId: string) {
  return useQuery({
    queryKey: thKeys.incident(incidentId),
    queryFn: () => trackingHealth.incident(incidentId),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    enabled: !!incidentId,
  });
}

export function useRunRecord(runId: string) {
  return useQuery({
    queryKey: thKeys.run(runId),
    queryFn: () => trackingHealth.run(runId),
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
    enabled: !!runId,
  });
}

export function useEngines() {
  return useQuery({
    queryKey: thKeys.engines,
    queryFn: trackingHealth.engines,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
  });
}


export function useQueues() {
  return useQuery({
    queryKey: thKeys.queues,
    queryFn: trackingHealth.queues,
    staleTime: STALE_MS,
    refetchOnWindowFocus: false,
  });
}

/** The fleet-wide settings (backend ADR 0039). Fetched when the settings
 *  modal opens rather than with the page — the modal seeds its fields from
 *  the payload at mount, so it must hold the CURRENT row, not a minute-old
 *  one another operator may have moved. */
export function useTrackingSettings(enabled = true) {
  return useQuery({
    queryKey: thKeys.settings,
    queryFn: trackingHealth.settings,
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled,
  });
}

// ─── Mutations ───────────────────────────────────────────────────────────────
// Invalidation map (spec §8): incident writes → incident + hotel + worklist +
// fleet list; locks/override/proof → hotel + queues + summary.
//
// The fleet list joined the incident scope on 2026-08-15, when the fleet
// table began rendering the incident's blocked-on note as its "Waiting on"
// tag. Before that no incident write was visible there, so the list was
// deliberately left out.

function errMessage(error: any, fallback: string): string {
  return error?.response?.data?.message ?? fallback;
}

function useInvalidate() {
  const qc = useQueryClient();
  return {
    incidentScope(customerId: string, incidentId?: string | null) {
      if (incidentId)
        qc.invalidateQueries({ queryKey: thKeys.incident(incidentId) });
      else qc.invalidateQueries({ queryKey: ["th", "incident"] });
      qc.invalidateQueries({ queryKey: thKeys.hotel(customerId) });
      qc.invalidateQueries({ queryKey: ["th", "worklist"] });
      qc.invalidateQueries({ queryKey: ["th", "hotels"] });
      qc.invalidateQueries({ queryKey: thKeys.summary });
    },
    governanceScope(customerId: string) {
      qc.invalidateQueries({ queryKey: thKeys.hotel(customerId) });
      qc.invalidateQueries({ queryKey: thKeys.queues });
      qc.invalidateQueries({ queryKey: thKeys.summary });
      qc.invalidateQueries({ queryKey: ["th", "worklist"] });
      qc.invalidateQueries({ queryKey: ["th", "hotels"] });
    },
  };
}

/** Long-running synchronous mutation (~14–84s measured): keep the button
 *  pending. The response is the run OUTCOME (runId + outcome counts) — the
 *  finished run record's cells come from `GET runs/:runId` afterwards. */
export function useDiagnoseNow(customerId: string) {
  const inv = useInvalidate();
  return useMutation<DiagnoseRunOutcome, any, { dryRun?: boolean } | void>({
    mutationFn: (body) => trackingHealth.diagnoseNow(customerId, body || {}),
    onSuccess: (outcome) => {
      // A 200 is not always a run. A Validator-locked hotel answers
      // `skipped-validator-lock` with no runId, and "0 findings" on that
      // reads as a clean bill of health for a hotel nothing looked at.
      if (!outcome?.runId || outcome.runStatus !== "completed") {
        ShowToast({
          type: "error",
          message:
            outcome?.runStatus === "skipped-validator-lock"
              ? "No run — this hotel has a Validator lock. Remove it to diagnose."
              : `No run — the Validator answered "${outcome?.runStatus ?? "nothing"}".`,
        });
        inv.incidentScope(customerId);
        return;
      }
      const findings = outcome.outcomes?.finding ?? 0;
      ShowToast({
        type: "success",
        message: `Validator run complete — ${findings} finding${findings === 1 ? "" : "s"}`,
      });
      inv.incidentScope(customerId);
    },
    onError: (error) => {
      ShowToast({
        type: "error",
        message: errMessage(
          error,
          "Run request failed — if this was a timeout, the run may have completed server-side; refresh the hotel page.",
        ),
      });
      // A network timeout is not a failed run: refresh so lastRun surfaces.
      inv.incidentScope(customerId);
    },
  });
}

/**
 * A blocked-on note, optionally scoped to one problem on the active plan.
 *
 * The scope is a LABEL, not a second clock: a scoped note groups under the
 * problem it names in the blocked-on log, while the incident keeps its single
 * blocked-on note and its single 7-day reminder. Giving each problem its own
 * clock would multiply the reminder cadence by the length of the plan.
 */
export function useLogNote(customerId: string, incidentId?: string | null) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: { note: string; problem?: string }) =>
      trackingHealth.logNote(customerId, vars),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Note logged — reminder clock reset" });
      inv.incidentScope(customerId, incidentId);
    },
    onError: (error) =>
      ShowToast({ type: "error", message: errMessage(error, "Failed to log note") }),
  });
}

/**
 * Silence the incident (ADR 0017) — the disposition that replaced the retired
 * false-positive close. Invalidates the incident scope AND the governance
 * scope: a standing silence is listed on Queues, and the two surfaces must
 * never disagree about what is currently deferred.
 */
export function useSilenceIncident(
  customerId: string,
  incidentId?: string | null,
) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: {
      days: number;
      note?: string;
      cause?: SilenceCause;
    }) => trackingHealth.silenceIncident(customerId, body),
    onSuccess: (_data, vars) => {
      ShowToast({
        type: "success",
        message: `Silenced for ${vars.days} day${
          vars.days === 1 ? "" : "s"
        } — the incident stays open and the hotel stays Unhealthy`,
      });
      inv.incidentScope(customerId, incidentId);
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to silence reminder"),
      }),
  });
}

export function useClearSilence(
  customerId: string,
  incidentId?: string | null,
) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { note?: string } | void) =>
      trackingHealth.clearSilence(customerId, body || {}),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Silence ended — reminder back on its ordinary clock",
      });
      inv.incidentScope(customerId, incidentId);
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to end silence"),
      }),
  });
}

/**
 * Correct the wording of a logged fix or note. Words only — no run, no
 * resolution, and the reminder clock stays where it was.
 */
export function useEditIncidentNote(
  customerId: string,
  incidentId?: string | null,
) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { eventId: string; note: string }) =>
      trackingHealth.editIncidentNote(customerId, body.eventId, {
        note: body.note,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Entry updated" });
      inv.incidentScope(customerId, incidentId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to update the entry"),
      }),
  });
}

/**
 * Hide one history row from the reads a human uses.
 *
 * Invalidates the GOVERNANCE scope as well as the incident scope: the four
 * histories are spread across the hotel page, the incident thread and Queues,
 * and a row still showing on one surface after being removed on another is
 * the exact confusion this feature exists to end.
 *
 * A 409 here is usually the standing-silence refusal — the server is saying
 * "clear the silence first", not "no".
 */
export function useRemoveHistoryRow(
  customerId: string,
  incidentId?: string | null,
) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { log: HistoryLog; rowId: string; reason: string }) =>
      trackingHealth.removeHistoryRow(customerId, body.log, body.rowId, {
        reason: body.reason,
      }),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Row removed from the history — nothing else changed",
      });
      inv.incidentScope(customerId, incidentId);
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to remove the row"),
      }),
  });
}

export function useFixLog(customerId: string, incidentId?: string | null) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { problem: string; note: string }) =>
      trackingHealth.fixLog(customerId, body),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Fix logged" });
      inv.incidentScope(customerId, incidentId);
    },
    onError: (error) =>
      ShowToast({ type: "error", message: errMessage(error, "Failed to log fix") }),
  });
}

/**
 * Move an incident between statuses.
 *
 * **Currently has no caller** — kept deliberately, not dead by accident. The
 * false-positive close was its only user, and status is otherwise DERIVED: the
 * Solver sets `fix_identified` / `waiting_on_others` / `research_queue` from
 * the next unexecuted step's label, and the fix log drives `fix_applied`. The
 * endpoint still exists and still accepts the hand-off statuses with their
 * mandatory blocked-on note, so this stays as the binding for the day a manual
 * hand-off UI is wanted. Both CLOSED statuses are refused server-side.
 */
export function useIncidentTransition(
  customerId: string,
  incidentId?: string | null,
) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: {
      to: IncidentStatus;
      reason: string;
      note?: string;
    }) => trackingHealth.transition(customerId, body),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Incident updated" });
      inv.incidentScope(customerId, incidentId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to update incident"),
      }),
  });
}

export function useRefuteHypothesis(
  customerId: string,
  incidentId?: string | null,
) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { hypothesis: string; key?: string; note?: string }) =>
      trackingHealth.refuteHypothesis(customerId, body),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Refutation logged — later briefs will not re-raise it",
      });
      inv.incidentScope(customerId, incidentId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to log refutation"),
      }),
  });
}

export function useInvestigate(customerId: string, incidentId?: string | null) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: () => trackingHealth.investigate(customerId),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Investigation brief re-run" });
      inv.incidentScope(customerId, incidentId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to re-run the brief"),
      }),
  });
}

export function useAddCondition(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: {
      scopeType: ConditionScopeType;
      scopeId: string;
      reasonCategory: ConditionReasonCategory;
      reason: string;
      exitCondition?: string;
    }) => trackingHealth.addCondition(customerId, body),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Condition accepted" });
      inv.governanceScope(customerId);
      inv.incidentScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to accept condition"),
      }),
  });
}

export function useRemoveCondition(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: { conditionId: string; reason: string }) =>
      trackingHealth.removeCondition(customerId, vars.conditionId, {
        reason: vars.reason,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Condition removed" });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to remove condition"),
      }),
  });
}

export function useConfirmCondition(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: { conditionId: string; note?: string }) =>
      trackingHealth.confirmCondition(customerId, vars.conditionId, {
        note: vars.note,
      }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Condition re-confirmed" });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to confirm condition"),
      }),
  });
}

export function useReconfirmDispositions(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (note?: string) =>
      trackingHealth.reconfirmDispositions(customerId, { note }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Dispositions re-confirmed" });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to re-confirm dispositions"),
      }),
  });
}

export function useApplyLock(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: { lock: TrackingLockKind; reason: string }) =>
      trackingHealth.applyLock(customerId, vars.lock, { reason: vars.reason }),
    onSuccess: (_d, vars) => {
      ShowToast({ type: "success", message: `${vars.lock} lock applied` });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to apply lock"),
      }),
  });
}

export function useRemoveLock(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: { lock: TrackingLockKind; reason: string }) =>
      trackingHealth.removeLock(customerId, vars.lock, { reason: vars.reason }),
    onSuccess: (data, vars) => {
      ShowToast({
        type: "success",
        message: data?.tdsdReset
          ? `${vars.lock} lock removed — trusted data start date reset (${data.skippedRuns ?? 0} skipped runs)`
          : `${vars.lock} lock removed — trusted data start date unchanged`,
      });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to remove lock"),
      }),
  });
}

/** Fleet-table variants: the row decides the hotel, so `customerId` rides
 *  the mutation variables instead of binding the hook. */
export function useApplyLockFor() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: {
      customerId: string;
      lock: TrackingLockKind;
      reason: string;
    }) =>
      trackingHealth.applyLock(vars.customerId, vars.lock, {
        reason: vars.reason,
      }),
    onSuccess: (_d, vars) => {
      ShowToast({ type: "success", message: `${vars.lock} lock applied` });
      inv.governanceScope(vars.customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to apply lock"),
      }),
  });
}

export function useRemoveLockFor() {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (vars: {
      customerId: string;
      lock: TrackingLockKind;
      reason: string;
    }): Promise<{ tdsdReset: boolean; skippedRuns: number | null }> =>
      trackingHealth.removeLock(vars.customerId, vars.lock, {
        reason: vars.reason,
      }),
    onSuccess: (data, vars) => {
      ShowToast({
        type: "success",
        message: data?.tdsdReset
          ? `${vars.lock} lock removed — trusted data start date reset (${data.skippedRuns ?? 0} skipped runs)`
          : `${vars.lock} lock removed — trusted data start date unchanged`,
      });
      inv.governanceScope(vars.customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to remove lock"),
      }),
  });
}

export function useApplyOverride(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: {
      state: OverrideState;
      reason: string;
      note: string;
    }) => trackingHealth.applyOverride(customerId, body),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Status override set" });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to set override"),
      }),
  });
}

export function useClearOverride(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (reason: string) =>
      trackingHealth.clearOverride(customerId, { reason }),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Status override cleared" });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to clear override"),
      }),
  });
}

export function useSetProofRequirement(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: { count: number | null; reason: string }) =>
      trackingHealth.setProofRequirement(customerId, body),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Proof requirement updated" });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to update proof requirement"),
      }),
  });
}

export function useSetAccessMode(customerId: string) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: (body: {
      mode: TrackingAccessMode;
      note?: string;
      reason: string;
    }) => trackingHealth.setAccessMode(customerId, body),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Access mode recorded" });
      inv.governanceScope(customerId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to record the access mode"),
      }),
  });
}

/**
 * Change the fleet-wide settings (backend ADR 0039).
 *
 * Nothing about the fleet moves on save: the derived Low-volume bucket and
 * Rule 6 are re-read at the next daily pass, which the toast says in so many
 * words — an operator who lowers the threshold and refreshes the fleet table
 * would otherwise take the unchanged section for a failed save. The settings
 * read is invalidated so the modal's footer (who changed it, version) is
 * current the next time it opens; the summary and hotels reads are
 * invalidated alongside so nothing on the page can be older than the setting
 * it is being read against, even though both will look the same until
 * tomorrow. Server refusals (unknown key, out of bounds, no change) arrive as
 * the server's own sentence.
 */
export function useUpdateTrackingSettings() {
  const qc = useQueryClient();
  return useMutation<TrackingSettingsPayload, any, UpdateTrackingSettingsBody>({
    mutationFn: (body) => trackingHealth.updateSettings(body),
    onSuccess: () => {
      ShowToast({
        type: "success",
        message: "Settings saved — takes effect on the next daily pass",
      });
      qc.invalidateQueries({ queryKey: thKeys.settings });
      qc.invalidateQueries({ queryKey: thKeys.summary });
      qc.invalidateQueries({ queryKey: ["th", "hotels"] });
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to save settings"),
      }),
  });
}

export function usePlanStepAction(
  customerId: string,
  incidentId?: string | null,
) {
  const inv = useInvalidate();
  return useMutation({
    mutationFn: async (vars: {
      stepIndex: number;
      action: FixStepAction;
      note?: string;
      /**
       * `Apply this change` is approve followed by execute — execute is
       * reachable only from approved (the human gate is structural).
       *
       * The second call is the one that touches the account, and it can fail
       * (409 refused / 502 rejected). When it does, this mutation rejects and
       * the step stays `approved` server-side — which is the point: a failed
       * write must not leave a step looking carried out. The operator sees the
       * server's own sentence and the button is still there to try again.
       */
      thenExecute?: boolean;
    }) => {
      const first = await trackingHealth.planStepAction(
        customerId,
        vars.stepIndex,
        vars.action,
        { note: vars.note },
      );
      if (vars.thenExecute && vars.action === "approve") {
        return trackingHealth.planStepAction(
          customerId,
          vars.stepIndex,
          "execute",
          {},
        );
      }
      return first;
    },
    onSuccess: (_d, vars) => {
      // The two routes claim different things and must not share a sentence:
      // one changed the account, the other recorded that a person did.
      const executed =
        vars.action === "execute" ||
        (vars.thenExecute && vars.action === "approve");
      ShowToast({
        type: "success",
        message: executed
          ? "Change applied to the account"
          : vars.action === "complete"
            ? "Recorded — thanks"
            : `Step ${vars.action} recorded`,
      });
      inv.incidentScope(customerId, incidentId);
    },
    onError: (error) =>
      ShowToast({
        type: "error",
        message: errMessage(error, "Failed to update fix step"),
      }),
  });
}
